import React from 'react';
import { ShieldCheck } from 'lucide-react';

import { FormattedMessage } from '../../util/reactIntl';

import { Badge } from '../ui/badge';
import { cn } from '../ui/utils';

export const MEMBERSHIP_BASIC = 1 as const;
export const MEMBERSHIP_PRO = 2 as const;
export const MEMBERSHIP_BUSINESS = 3 as const;

const PAID_TIER_TRANSLATION_KEYS: Record<number, string> = {
  [MEMBERSHIP_PRO]: 'SellerBadges.tier.pro',
  [MEMBERSHIP_BUSINESS]: 'SellerBadges.tier.business',
};

/**
 * Resolve a raw `metadata.membershipTier` value into a known tier number.
 * Anything outside the defined enum falls back to the basic tier.
 *
 * @param raw - The raw value pulled from `user.attributes.profile.metadata.membershipTier`
 * @returns A safe tier number: one of MEMBERSHIP_BASIC / PRO / BUSINESS.
 */
export const resolveMembershipTier = (raw: unknown): number => {
  return raw === MEMBERSHIP_PRO || raw === MEMBERSHIP_BUSINESS ? raw : MEMBERSHIP_BASIC;
};

/**
 * Whether a tier number is a paid tier (drives the membership badge visibility).
 */
export const isPaidTier = (tier: number): boolean => tier >= MEMBERSHIP_PRO;

/**
 * Pull membership state directly from a Sharetribe user entity. Convenience
 * wrapper so callers don't repeat the metadata path everywhere.
 */
export const getUserMembership = (user: any): { tier: number; isVerified: boolean } => {
  const metadata = user?.attributes?.profile?.metadata || {};
  return {
    tier: resolveMembershipTier(metadata.membershipTier),
    isVerified: !!metadata.isIdentityVerified,
  };
};

type SellerBadgesProps = {
  membershipTier: number;
  isVerified: boolean;
  /** `compact` is for inline contexts like listing cards; `default` for hero/profile contexts. */
  size?: 'compact' | 'default';
  className?: string;
};

const COMPACT_BADGE_CLS =
  'gap-1 px-1.5 py-0.5 text-[10px] tracking-[0.16em]';

type VerifiedSellerBadgeProps = {
  size?: 'compact' | 'default';
  className?: string;
};

/**
 * Standalone "Verified seller" badge — Stripe Identity outcome, independent
 * of paid membership tier. A Basic user who paid the one-time fee and passed
 * verification gets this. So does a Pro/Enterprise user who verified. The
 * tier badge (amber, paid-only) and this badge (blue, identity-only) are
 * always rendered as a pair via SellerBadges, but the verified badge is
 * exported on its own for surfaces like IdentityStatusCard.
 */
export const VerifiedSellerBadge = ({
  size = 'default',
  className,
}: VerifiedSellerBadgeProps) => {
  if (size === 'compact') {
    return (
      <span
        title="Verified Seller"
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 ring-1 ring-inset ring-blue-500/20',
          className
        )}
        data-testid="seller-verified-badge"
      >
        <ShieldCheck className="h-3 w-3" />
      </span>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn('gap-1.5 border-blue-200 bg-blue-50 text-blue-700', className)}
      data-testid="seller-verified-badge"
    >
      <ShieldCheck className="h-3.5 w-3.5" />
      <FormattedMessage id="SellerBadges.verifiedSeller" />
    </Badge>
  );
};

const SellerBadges = ({
  membershipTier,
  isVerified,
  size = 'default',
  className,
}: SellerBadgesProps) => {
  const tierKey = PAID_TIER_TRANSLATION_KEYS[membershipTier];
  const showVerified = isVerified;

  if (!tierKey && !showVerified) return null;

  const badgeSizeCls = size === 'compact' ? COMPACT_BADGE_CLS : '';

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {tierKey ? (
        <Badge
          className={cn(
            'gap-1 bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/20 hover:bg-amber-500/10',
            badgeSizeCls
          )}
          data-testid="seller-membership-badge"
        >
          <ShieldCheck className={cn(size === 'compact' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
          <FormattedMessage id={tierKey} />
        </Badge>
      ) : null}
      {showVerified ? <VerifiedSellerBadge size={size} /> : null}
    </span>
  );
};

export default SellerBadges;
